import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
import { institutionLabel } from '@/shared/constants/institutions';
import guidelinesService from '@/shared/services/guidelinesService';
import { useInstitutionPolicy } from '@/shared/hooks/useInstitutionPolicy';
import { renderMarkdown } from '@/shared/utils/markdown';
import '../listings.css';

function Markdown({ text }) {
    return (
        <div
            dangerouslySetInnerHTML={{
                __html: renderMarkdown(text),
            }}
        />
    );
}

Markdown.propTypes = { text: PropTypes.string.isRequired };

/**
 * The reuse guidelines and the poster's institution policy, always open.
 *
 * The rules are the point of the page, so they are not behind a disclosure:
 * on wide screens the panel stays beside the form, and on narrow ones it
 * sits above it. The guidelines text is the shared section plus the poster's
 * institution section, as the guidelines API merges them; the capital
 * equipment and asset tag summaries come from the same institution's policy.
 * A guidelines outage says so and does not stop a post.
 */
function GuidelinesPanel({ institution }) {
    const [text, setText] = useState('');
    const [failed, setFailed] = useState(false);
    const { policy } = useInstitutionPolicy(institution);

    useEffect(() => {
        let cancelled = false;
        guidelinesService
            .get()
            .then((data) => {
                if (!cancelled) {
                    setText(data.forMe?.text || '');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const capital = policy?.capitalEquipment;
    const assetTags = policy?.assetTags;

    return (
        <section
            aria-labelledby="listing-guidelines-heading"
            className="afe-guidelines-sticky"
            // The column scrolls on its own when it is taller than the
            // viewport, so keyboard users need to be able to reach it.
            tabIndex={0}
        >
            <Card>
                <Card.Body>
                    <h2 className="h5" id="listing-guidelines-heading">
                        Reuse guidelines for {institutionLabel(institution)}
                    </h2>
                    {failed && (
                        <p className="text-muted">
                            Guidelines could not be loaded right now. You can
                            still post.
                        </p>
                    )}
                    {!failed && text && <Markdown text={text} />}
                    {capital && (
                        <>
                            <h3 className="h6">Capital equipment policy</h3>
                            <p className="mb-1">
                                Items valued at $
                                {policy.capitalThresholdUsd.toLocaleString()} or
                                more are tracked assets.
                            </p>
                            <Markdown text={capital.text} />
                            {capital.link && (
                                <p>
                                    <a
                                        href={capital.link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {capital.link.label}
                                    </a>
                                </p>
                            )}
                        </>
                    )}
                    {assetTags && (
                        <>
                            <h3 className="h6">Asset tag policy</h3>
                            <Markdown text={assetTags.text} />
                        </>
                    )}
                </Card.Body>
            </Card>
        </section>
    );
}

GuidelinesPanel.propTypes = { institution: PropTypes.string };

export default GuidelinesPanel;
